import axios from 'axios';

// Constants
const DEMO_MODE = process.env.REACT_APP_BUILD_MODE === 'development';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'app.reasonshield.com';

// Auth service interfaces
interface AuthToken {
  token: string;
  expiresAt: number;
}

export interface User {
  sub: string;
  email: string;
  groups: string[];
  [key: string]: any;
}

/**
 * Authentication service for the vendor portal
 * Handles Cognito authentication in production and mock auth in development
 */
class AuthService {
  private token: string | null = null;
  private user: User | null = null;
  private refreshTokenTimeout: NodeJS.Timeout | null = null;
  private apiBaseUrl = API_BASE_URL;
  
  // NEW_PASSWORD_REQUIRED challenge handling
  private session: string | null = null;
  private passwordChangeRequired: boolean = false;
  private pendingUsername: string | null = null;

  constructor() {
    console.log(`Auth service initialized in ${DEMO_MODE ? 'development' : 'production'} mode`);
    this.loadTokenFromSessionStorage();
  }

  private loadTokenFromSessionStorage(): void {
    try {
      const storedToken = sessionStorage.getItem('auth_token');
      
      if (storedToken) {
        const authData = JSON.parse(storedToken) as AuthToken;
        
        // Check if token has expired
        if (authData.expiresAt > Date.now()) {
          this.token = authData.token;
          console.log('Loaded valid token from session storage');
          
          // Also try to load user data
          const userData = sessionStorage.getItem('user_data');
          if (userData) {
            this.user = JSON.parse(userData);
            console.log('Loaded user data from session storage');
          }
          
          // Set up token refresh if needed
          this.setupTokenRefresh(authData.expiresAt);
        } else {
          console.log('Stored token is expired');
          this.clearAuth();
        }
      }
    } catch (error) {
      console.error('Error loading token from session storage:', error);
      this.clearAuth();
    }
  }

  private storeTokenInSessionStorage(token: string, expiresIn: number): void {
    try {
      const expiresAt = Date.now() + expiresIn * 1000;
      
      // Store the token with expiration
      const authData: AuthToken = {
        token,
        expiresAt
      };
      
      sessionStorage.setItem('auth_token', JSON.stringify(authData));
      console.log('Token stored in session storage');
      
      // Set up token refresh
      this.setupTokenRefresh(expiresAt);
    } catch (error) {
      console.error('Error storing token in session storage:', error);
    }
  }

  private setupTokenRefresh(expiresAt: number): void {
    // Clear any existing timeout
    if (this.refreshTokenTimeout) {
      clearTimeout(this.refreshTokenTimeout);
    }
    
    // Calculate time until refresh (5 minutes before expiry)
    const timeUntilRefresh = expiresAt - Date.now() - (5 * 60 * 1000);
    
    if (timeUntilRefresh > 0) {
      console.log(`Token refresh scheduled in ${Math.round(timeUntilRefresh/1000)} seconds`);
      this.refreshTokenTimeout = setTimeout(() => {
        this.refreshToken();
      }, timeUntilRefresh);
    }
  }

  async login(email: string, password: string): Promise<boolean> {
    try {
      // Log the API URL we're using for debugging
      console.log(`Using API URL: ${this.apiBaseUrl}`);
      
      // Call the API endpoint to verify credentials with Cognito
      console.log(`Sending auth request to: ${this.apiBaseUrl}/api/v1/lis-portal/auth/verify`);
      
      // Try with the primary API URL (could be app.reasonshield.com or localhost)
      try {
        // Ensure we're using HTTPS if we're on reasonshield.com
        let url = this.apiBaseUrl;
        if (url.includes('reasonshield.com') && url.startsWith('http:')) {
          url = url.replace('http:', 'https:');
          console.log(`Switched to HTTPS URL: ${url}`);
        }
        
        const response = await axios.post(`${url}/api/v1/lis-portal/auth/verify`, {
          username: email,
          password
        });

        // Check if NEW_PASSWORD_REQUIRED challenge is returned
        if (response.data.challengeName === 'NEW_PASSWORD_REQUIRED') {
          console.log('Password change required');
          this.passwordChangeRequired = true;
          this.session = response.data.session;
          this.pendingUsername = email;
          
          // Return false but with the challenge flag set
          return false;
        }
        
        if (response.data.success) {
          this.token = response.data.token;
          this.user = response.data.user;
          
          // Store user data
          sessionStorage.setItem('user_data', JSON.stringify(this.user));
          
          // Store token with expiry
          if (this.token) {
            this.storeTokenInSessionStorage(this.token, response.data.expiresIn || 3600);
          }
          return true;
        }
        
        return false;
      } catch (error) {
        console.error('Error making auth request:', error);
        
        // For demo mode (or when API is unreachable), provide fallback authentication
        if (DEMO_MODE || window.location.hostname === 'localhost') {
          console.log('Using demo authentication as fallback');

          if ((email === 'admin@example.com' && password === 'Admin123!') ||
              (email === 'vendor@example.com' && password === 'Vendor123!') ||
              (email === 'test@example.com' && password === 'Test123!')) {
            console.log('Demo login successful');
            this.token = 'demo_token';
            this.user = {
              sub: 'demo_user_id',
              email: email,
              name: email.split('@')[0].charAt(0).toUpperCase() + email.split('@')[0].slice(1),
              groups: ['vendors']
            };
            
            // Store user data
            sessionStorage.setItem('user_data', JSON.stringify(this.user));
            
            // Store token with expiry
            this.storeTokenInSessionStorage(this.token, 3600);
            return true;
          }
          
          console.log('Demo login failed - invalid credentials');
          return false;
        }

        throw error; // Re-throw the original error
      }
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  }

  /**
   * Checks if password change is required (NEW_PASSWORD_REQUIRED challenge)
   */
  isPasswordChangeRequired(): boolean {
    return this.passwordChangeRequired;
  }

  /**
   * Complete the new password challenge
   * @param newPassword The new password to set
   * @param userAttributes Optional user attributes to set
   */
  async completeNewPasswordChallenge(newPassword: string, userAttributes?: Record<string, string>): Promise<boolean> {
    try {
      if (!this.pendingUsername || !this.session) {
        console.error('No pending password change');
        return false;
      }

      // Create local copies that we know are not null
      const username = this.pendingUsername;
      const session = this.session;

      if (DEMO_MODE) {
        console.log('Demo mode: Simulating password change success');
        this.passwordChangeRequired = false;
        this.session = null;
        
        // Simulate successful login
        this.token = 'demo_token';
        this.user = {
          sub: 'demo_user_id',
          email: username,
          name: username.split('@')[0].charAt(0).toUpperCase() + username.split('@')[0].slice(1),
          groups: ['vendors']
        };
        
        // Store user data
        sessionStorage.setItem('user_data', JSON.stringify(this.user));
        
        // Store token with a 1-hour expiry
        this.storeTokenInSessionStorage('demo_token', 3600);
        
        this.pendingUsername = null;
        return true;
      } else {
        // Production mode - Call the API to complete the challenge
        const response = await axios.post(`${this.apiBaseUrl}/api/v1/lis-portal/auth/change-password`, {
          username,
          new_password: newPassword,
          session,
          user_attributes: userAttributes || {}
        });
        
        if (response.data.success) {
          // Reset challenge state
          this.passwordChangeRequired = false;
          this.session = null;
          this.pendingUsername = null;
          
          // Set auth token and user data
          const idToken = response.data.id_token;
          if (idToken) {
            this.token = idToken;
            
            // Get user info from token or API
            await this.getUserProfile();
            
            // Store token with expiry
            this.storeTokenInSessionStorage(idToken, response.data.expires_in || 3600);
          }
          
          return true;
        }
        
        return false;
      }
    } catch (error) {
      console.error('Password change error:', error);
      return false;
    }
  }

  logout(): void {
    this.clearAuth();
    // Redirect to login page with the correct path prefix
    window.location.href = '/vendor-portal/login';
  }

  private clearAuth(): void {
    // Clear auth data
    this.token = null;
    this.user = null;
    this.passwordChangeRequired = false;
    this.session = null;
    this.pendingUsername = null;
    
    // Clear session storage
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('user_data');
    
    // Clear any refresh timeout
    if (this.refreshTokenTimeout) {
      clearTimeout(this.refreshTokenTimeout);
      this.refreshTokenTimeout = null;
    }
    
    console.log('Authentication cleared');
  }

  isAuthenticated(): boolean {
    return !!this.token && !this.passwordChangeRequired;
  }

  getToken(): string | null {
    return this.token;
  }

  async refreshToken(): Promise<boolean> {
    // In this demo app, we don't actually refresh the token
    // In a real app, you would call Cognito's token refresh endpoint
    console.log('Token refresh not implemented');
    return true;
  }

  async getUserProfile(): Promise<User | null> {
    try {
      if (!this.token) {
        return null;
      }
      
      if (DEMO_MODE) {
        // If we already have user data with email, use that to derive a name
        if (this.user && this.user.email) {
          const email = this.user.email;
          const name = email.split('@')[0].charAt(0).toUpperCase() + email.split('@')[0].slice(1);
          
          this.user = {
            ...this.user,
            name: name
          };
        } else {
          // Fallback to demo user if no existing user data
          this.user = {
            sub: 'demo_user_id',
            email: 'demo@example.com',
            name: 'Demo User',
            groups: ['vendors']
          };
        }
        
        // Store user data
        sessionStorage.setItem('user_data', JSON.stringify(this.user));
        
        return this.user;
      } else {
        // Get user profile from API
        const response = await axios.get(`${this.apiBaseUrl}/api/v1/lis-portal/vendor-profile`, {
          headers: this.getAuthHeaders()
        });
        
        if (response.data.success) {
          this.user = response.data.profile;
          
          // Store user data
          sessionStorage.setItem('user_data', JSON.stringify(this.user));
          
          return this.user;
        }
        
        return null;
      }
    } catch (error) {
      console.error('Error getting user profile:', error);
      return null;
    }
  }

  getAuthHeaders(): Record<string, string> {
    if (!this.token) {
      return {};
    }
    
    return {
      'Authorization': `Bearer ${this.token}`
    };
  }

  getCurrentUser(): User | null {
    return this.user;
  }
}

// Create a singleton instance
const authService = new AuthService();
export default authService; 